import { useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import { useTranslation } from "react-i18next";
import { TextField } from 'formik-mui';
import { LinearProgress } from '@mui/material';
import { Field, Form, Formik } from 'formik';
import i18next from 'i18next';

import Settings from '../../data/settings';
import { login } from '../../api';
import { AuthToken } from '../../models/AuthToken';
import { AuthTypes } from '../../models';

export interface EmailValidationValues {
  email: string;
}

export const validateEmail = (values: EmailValidationValues) => {
  const errors: Partial<EmailValidationValues> = {};

  if (!values.email) {
    errors.email = i18next.t("required-field") as string;
  } else if (
    !/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i.test(values.email)
  ) {
    errors.email = i18next.t("invalid-email") as string;
  }
  return errors;
}

export interface SignInProps {
  onSignedIn?: (data: AuthToken) => void;
  onForgottenPassword?: () => void;
  hideTitle?: boolean;
  initialValues?: InitialValues;
}

export interface InitialValues {
  email?: string;
  password?: string;
}

export default function SignIn({ hideTitle, initialValues, onSignedIn, onForgottenPassword }: SignInProps) {
  const { t } = useTranslation("translation");

  const [error, setError] = useState<string | undefined | null>(undefined);

  const defaultValues = {
    email: initialValues?.email || '',
    password: initialValues?.password || ''
  };

  return (
    <div className="auth-component">
      {!hideTitle && <h1>{t("account-login")}</h1>}
      <h2>{t("sign-in-desc")}</h2>

      {Settings.auth && Settings.auth.type === AuthTypes.oauth2 && <>
        <p className="centered-label">{t("enter-credentials")}</p>

        <Formik
          initialValues={defaultValues}
          enableReinitialize={true}
          validate={validateEmail}
          onSubmit={async (values, { setSubmitting }) => {
            setError(undefined);

            setSubmitting(true);

            const response = await login(values.email, values.password);

            if (!response)
              setError(t("internet-disconnected"));
            else if (response.status !== 200)
              setError(t("invalid-credentials"));
            else {
              const { data } = response;

              if (onSignedIn)
                onSignedIn({ access_token: data });
            }
          }}
        >
          {({ submitForm, isSubmitting }) => (
            <Form>
              <Field component={TextField} name="email" type="email" label="Email"
                helperText={t("enter-email")} />

              <Field component={TextField} type="password"
                label="Password" name="password"
                helperText={t("enter-password")} />

              <Button type='submit'
                className="signin-button" variant="contained"
                disabled={isSubmitting} onClick={submitForm}>
                {t("sign-in")}
              </Button>

              <div style={{ display: "flex", justifyContent: "space-around", alignItems: "center" }}>
                <Button className="link-button" onClick={() => !isSubmitting && onForgottenPassword ? onForgottenPassword() : ""}>{t("forgot-password")}</Button>
              </div>

              <LinearProgress style={{ marginTop: "1em", opacity: isSubmitting ? 1 : 0 }} />
            </Form>
          )}
        </Formik>

        {error &&
          <Box marginBottom={3}>
            <p className="error-message">{error}</p>
          </Box>}

        <Box marginLeft={5} marginRight={5}>
          <p className="centered-label">{t("allow-cookies")}</p>
        </Box>
      </>}
    </div>
  );
}
